import Button from 'components/shared/app/Button';
import DateRangePicker from 'components/shared/app/DateRangePicker';
import Dialog from 'components/shared/app/Dialog';
import Dropdown from 'components/shared/app/Dropdown';
import IconCopy from 'components/shared/app/icons/IconCopy';
import { ErrorMessages } from 'lib/AppErrors';
import Config from 'lib/Config';
import { Totals } from 'lib/data/models/CampaignObjects';
import { ProviderNames } from 'lib/data/models/Providers';
import { PageHelper } from 'lib/helpers/PageHelper';
import { ManagedRequest, RequestStatus } from 'lib/RequestManager';
import NotificationService from 'lib/services/NotificationService';
import { copyToClipboard, getAccountProviders, getAccountTimezone, getInnerError, renderAdminContent, renderError, renderLoader, renderNoDataMessage } from 'lib/utils/adaya/AdayaUtils';
import { getNormalizedUrlParams } from 'lib/utils/BrowserUtils';
import LocalStorage from 'lib/utils/LocalStorage';
import { capitalize } from 'lib/utils/StrUtils';
import { CampaignDataFilter } from 'models/CampaignObjects';
import React, { useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import AccountDataService from 'services/data/AccountDataService';
import { renderName } from 'utils/adaya/CampaignUtils';
import { combineAllDailyCampaignData, filterAdObjects } from 'utils/adaya/ProviderUtils';
import { formatCurrency } from 'utils/currency';
import { createIsoTimeRange, formatIsoShort, getStoreDate, todayStart } from 'utils/DateUtils';
import { getLogger } from 'utils/logging';
import { orZero } from 'utils/NumUtils';
import { allMetricOptions, allPerformanceOptions, goalMetricOptions, highLowPerformanceOptions } from './config';
import './style.scss';
import { sortCampaigns } from './utils';

const { log, warn } = getLogger('AdsManager', { color: 'yellow', enabled: true });

const pageHelper = new PageHelper();

// persist some page-specific filter settings to localstorage for ease-of-use:
const REFRESH_TIME_LIMIT_SECS = 30;
const QAM_LAST_REFRESHED = 'qam-last-refreshed';
const QAM_FILTER_SETTINGS = 'qam-filter-settings';
let lastRefreshed = LocalStorage.get(QAM_LAST_REFRESHED);
let savedFilter = LocalStorage.get(QAM_FILTER_SETTINGS);
let currentFilterKey = undefined;

export default AdsManager = (props) => {

    const user = props.auth.user;
    const account = user?.activeAccount;
    const timezone = getAccountTimezone(account);
    const providers = getAccountProviders(account, [ProviderNames.SHOPIFY]).map(p => p.name);
    const navigate = useNavigate();
    log(`QAM providers`, providers);

    const params = getNormalizedUrlParams();
    const isDev = params.get('dev') == 'true';
    const isAdminUrlOverride = params.get('admin') == process.env.TEST_ACCOUNT_PASSWORD;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(undefined);
    const [data, setData] = useState(undefined); // raw data for current date range (before filters);
    const [shownData, setShownData] = useState(undefined); // filtered and sorted, final data
    const [shownTotals, setShownTotals] = useState(undefined); // filtered totals

    const [metricOptions, setMetricOptions] = useState(allMetricOptions);
    const [performanceOptions, setPerformanceOptions] = useState(allPerformanceOptions);
    const [metricOption, setMetricOption] = useState('ROAS'); //savedFilter?.metricOption ? savedFilter.metricOption : 'Revenue');
    const [performanceOption, setPerformanceOption] = useState('Highest'); //savedFilter?.performanceOption ? savedFilter.performanceOption : 'Highest');
    const [selectedDateRange, setSelectedDateRange] = useState({ selected: "today", dates: createIsoTimeRange('today', timezone, true) });
    const [activeTab, setActiveTab] = useState('campaigns');
    const [selectedCampaigns, setSelectedCampaigns] = useState([]);
    const [selectedAdsets, setSelectedAdsets] = useState([]);

    const [confirmDelete, setConfirmDelete] = useState(0);
    const [lastUpdated, setLastUpdated] = useState(undefined);
    const [activeOnly, setActiveOnly] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(false);
    const [deleteBeforeRefresh, setDeleteBeforeRefresh] = useState(false);

    ///////////////////////////////////////////////////////////////////////////

    // data options changed, fetch new data
    useEffect(async () => {
        fetchData();
    }, [user, selectedDateRange, activeOnly]);

    // data is downloaded and combined, now filter, sort, and show totals
    useEffect(async () => {
        updateViewData();
    }, [data, activeTab, performanceOption, metricOption]);

    const fetchData = async (fetchNewData = false) => {
        log(`fetchData`, fetchNewData);
        if (!account) return;
        setLoading(true);

        // @Michael: This is the method/approach for Landing Page data, just copy and uncomment on your page.
        // Or uncomment this try block and comment the one below to test this tracking data request.
        // try {
        //     const filter = getFilterFromSelection();
        //     filter.groupBy = EventGroupBy.LandingPage;
        //     let r = await pageHelper.getDailyTrackingDataRequest(
        //         account,
        //         filter,
        //         {
        //             dontFetchNewData: !fetchNewData, // QAM won't ask server for data by defaultErrorMessage
        //             resolveUnknownObjects: true, // let QAM resolve the unknown ad object IDs->names
        //             clearDataBefore: (fetchNewData && deleteBeforeRefresh),
        //             forceUpdate: forceUpdate
        //         }
        //     );
        //     onDataResponse(r);
        // } catch (e) {
        //     setError(getInnerError(e));
        // }

        try {
            let r = await pageHelper.getDailyCampaignDataRequest(
                getFilterFromSelection(),
                {
                    dontFetchNewData: !fetchNewData, // QAM won't ask server for data by defaultErrorMessage
                    resolveUnknownObjects: true, // let QAM resolve the unknown ad object IDs->names
                    clearDataBefore: (fetchNewData && deleteBeforeRefresh),
                    forceUpdate: forceUpdate
                }
            );
            onDataResponse(r);
        } catch (e) {
            setError(getInnerError(e));
        }

        setLoading(false);
    }

    // QAM needs all campaign data except Shopify
    const getFilterFromSelection = (): CampaignDataFilter => {
        const timeRange = {
            start_date: getStoreDate(selectedDateRange.dates[0], timezone).startOf('day').toISOString(),
            end_date: getStoreDate(selectedDateRange.dates[1], timezone).endOf('day').toISOString()
        }
        if (!account) throw `No account found for the current user.`;
        return { account, timeRange, providers };
    }

    // Handles the response in any way it needs. This should not return anything.
    const onDataResponse = (r: ManagedRequest): void => {
        try {
            if (!r) throw "Response was empty.";
            log('onDataResponse', r);

            if (r.status == RequestStatus.FINISHED) {
                // update UI data if the response matches the current request filter
                if (pageHelper.requestMatchesKey(r, getFilterFromSelection())) {
                    log(`Raw response:`, r.response, typeof r.response == 'object' ? `${Object.keys(r.response).length} days` : '(no data)');

                    let campaignData = combineAllDailyCampaignData(r.response);
                    if (activeOnly) {
                        campaignData = filterAdObjects(campaignData, (ao, aoType) => ao.totals?.adspend > 0, true);
                    }

                    // not ideal but the callback/event system seems to not understand/detect the state changes.
                    // if anyone has a solution please let me know, haven't had proper time. (RW)
                    // setData(undefined);
                    // setTimeout(() => {
                    setData(campaignData); // maybe it works?
                    //}, 0);
                } else {
                    log('Current request response does not match current select, not showing data. Request key:', r.key);
                }
            } else {
                // shouldn't get here, it's handled through exceptions mostly, but just in case.
                setError(getInnerError(r));
            }
        } catch (e) {
            log(`Exception`, e);
            throw e;
        }
    }

    const getDataSetLastUpdatedTime = (dataSet) => {
        return '';
    }

    // uses a timeout because god damnit react
    const updateViewData = () => {
        if (data) {
            setShownData(undefined);
            setShownTotals(undefined);

            setTimeout(() => {
                //scoreCampaignData(data);
                let sortedData = sortCampaigns(data?.campaigns, performanceOption, metricOption, user);

                setShownData(sortedData);
                setShownTotals(calculateShownTotals());
            });
        }
    }

    // calculates the bottom Totals row based on shown/selected data
    const calculateShownTotals = () => {
        let numItems = 0;

        // first get all totals for shown data only:
        let totals = new Totals();

        if (data && data.campaigns && data.campaigns.length) {
            if (activeTab == 'campaigns') {
                numItems = data.campaigns.length;
                data.campaigns.forEach(c => totals.addData(c.totals))
            } else {
                let campaigns = data.campaigns.filter(c => isCampaignFiltered(c));
                if (activeTab == 'adsets') {
                    campaigns.forEach(c => {
                        numItems += c.adsets.length;
                        c.adsets.forEach(a => totals.addData(a.totals));
                    })
                } else {
                    campaigns.forEach(c => c.adsets.filter(a => isAdsetFiltered(a))
                        .forEach(a => {
                            numItems += a.ads.length;
                            a.ads.forEach(ad => totals.addData(ad.totals))
                        }))
                }
            }
        }

        return {
            numItems,
            totals
        }
    }

    // called manually from app to refresh provider data.
    const refreshData = async () => {
        //let now = new Date();
        // We limit refresh to 1 minute on non-local environments:
        // if (lastRefreshed && !isLocalhost()) {
        //     // updated secs ago:
        //     let lrd = new Date(lastRefreshed);
        //     let secsAgo = parseInt(Math.abs(lrd.getTime() - now.getTime()) / 1000);
        //     if (secsAgo < REFRESH_TIME_LIMIT_SECS) {
        //         return alert(`Please manually refresh the data only once per ${REFRESH_TIME_LIMIT_SECS} seconds.\n` +
        //             `This only affects today's data. Past days will already be complete.\n` +
        //             `(Data was updated ${secsAgo} ${secsAgo == 1 ? 'second' : 'seconds'} ago)`);
        //     }
        //     console.log(`Refreshing... updating ${secsAgo}`);
        // }

        if (deleteBeforeRefresh) {
            if (!confirm("Are you SURE you want to delete all of the provider data in the current range, before re-fetching it?")) return;
        }

        lastRefreshed = (new Date()).toISOString();
        LocalStorage.set(QAM_LAST_REFRESHED, lastRefreshed);
        await fetchData(true);
    };

    // asks backend to delete all DailyAccountData on the account for the currently selected day.
    const deleteCurrentData_DEV_ONLY = async (localOnly = false) => {
        setConfirmDelete(localOnly ? 2 : 1);
    }

    const onConfirmDelete = async () => {
        setLoading(true);
        pageHelper.requestManager.deleteAllRequests(); // clear all requests because we don't really have an easy way to manage it.
        let filter = getFilterFromSelection();
        let deleteOnlyProviders = null; //[ { name: 'Facebook' }];
        let r = await AccountDataService.deleteProviderData(filter.timeRange.start_date, filter.timeRange.end_date, deleteOnlyProviders, confirmDelete == 2); // 2 == delete local data only
        LocalStorage.delete(QAM_LAST_REFRESHED);
        setLoading(false);
        setConfirmDelete(0);
        NotificationService.notifySuccess('Success', 'Data deleted. Reload the page or click the Refresh button to retrieve new data.', { duration: 10 });
    }

    const onChangeTimeFrame = async (result) => {
        console.log(`change time`, result)
        setSelectedDateRange(result);
    };

    // saves current filter selections to localstorage to be reload upon refresh.
    const persistFilterSettings = () => {
        let filterSettings = {
            selectedDateRange,
            performanceOption,
            metricOption
        }
        LocalStorage.set(QAM_FILTER_SETTINGS, filterSettings);
    };

    const onChangeMetricOption = (v) => {
        if (['Adspend', 'Revenue', 'Profit', 'Conv Rate', 'Orders'].includes(v)) {
            setPerformanceOptions(highLowPerformanceOptions);
        } else {
            setPerformanceOptions(allPerformanceOptions);
        }

        setMetricOption(v);
    };

    const onChangePerformanceOption = (v) => {
        if (v == 'Above Goal' || v == 'Below Goal') {
            setMetricOptions(goalMetricOptions);
            // default to metrics for goals:
            if (!['AOV', 'CPP', 'ROAS'].includes(metricOption)) {
                setMetricOption('AOV');
            }
        } else {
            setMetricOptions(allMetricOptions);
        }

        setPerformanceOption(v);
    };

    const selectCampaign = (cId, enabled) => {
        let sc = [...selectedCampaigns];
        if (enabled && !sc.includes(cId)) {
            sc.push(cId);
        } else if (!enabled && sc.includes(cId)) {
            sc.splice(sc.indexOf(cId), 1);
        }
        setSelectedCampaigns(sc);
    };

    const gotoCampaign = (e, campaign) => {
        e.preventDefault();
        setSelectedCampaigns([campaign.id || campaign.name]);
        setActiveTab('adsets');
    };

    const gotoAdset = (e, adset, campaign) => {
        e.preventDefault();
        setSelectedCampaigns([campaign.id || campaign.name]);
        setSelectedAdsets([adset.id || adset.name]);
        setActiveTab('ads');
    };

    const isCampaignSelected = (c) => {
        return selectedCampaigns.includes(c.id) || selectedCampaigns.includes(c.name);
    };

    const isAdsetSelected = (a) => {
        return selectedAdsets.includes(a.id) || selectedAdsets.includes(a.name);
    };

    const isCampaignFiltered = (c) => {
        if (activeTab == 'campaigns') return true; // show all campaigns on campaigns tab
        return selectedCampaigns?.length ? isCampaignSelected(c) : true;
    };

    const isAdsetFiltered = (a, campaign) => {
        if (activeTab == 'adsets') return true; // show all adsets on adsets tab
        if (activeTab == 'ads') return selectedAdsets?.length ? isAdsetSelected(a) : true;
        return true;
    };

    const renderCampaignRow = (c) => {
        return ([
            <div key={`${c.id}+${c.name}`} id={`${c.id}+${c.name}`} data-campaign-id={c.id} data-campaign-provider={c.provider} className={'row campaign-row data' +
                (isCampaignSelected(c) ? ' selected' : '') +
                (!c.provider ? ' provider-unknown' : ` provider-${c.provider}`) + (c.effective_status ? `${c.effective_status}` : '')
            }>

                {
                    (activeTab == 'campaigns') ?
                        <div className="item-name">
                            <input type="checkbox" id={c.id} checked={isCampaignSelected(c)} onChange={(e) => { selectCampaign(c.id, e.target.checked); }} />
                            <label className="label"><Link to="" onClick={(e) => { gotoCampaign(e, c); }}>{renderName(c.name || c.id)}</Link></label>
                        </div>
                        :
                        <div className="item-name">
                            <label className="label">{renderName(c.name)}</label>
                        </div>
                }

                <div className="row-data costs">
                    <div className="cell data">
                        {formatCurrency(c.totals.adspend, 2)}
                    </div>
                    <div className="cell data is-down">
                        {formatCurrency(c.totals.cpp, 2)}
                    </div>
                </div>

                <div className="row-data orders">
                    <div className="cell data">
                        {formatCurrency(c.totals.revenue, 2)}
                    </div>
                    <div className="cell data">
                        {c.totals.orders.toLocaleString("en-US")}
                    </div>
                    <div className="cell data is-up">
                        {formatCurrency(c.totals.aov, 2)}
                    </div>
                </div>

                <div className="row-data profit">
                    <div className="cell data">
                        {formatCurrency(c.totals.profit, 2)}
                    </div>
                    <div className="cell data">
                        {c.totals.roas.toFixed(2)}
                    </div>
                </div>

                <div className="row-data conversions">
                    <div className="cell data">
                        {c.totals.scores.score?.toFixed(0)}
                    </div>
                    <div className="cell data">
                        {orZero(c.totals.sessions).toLocaleString("en-US")}
                    </div>
                    <div className="cell data">
                        {orZero(c.totals.scores.conversionRate).toFixed(1)}%
                    </div>
                    <div className="cell data">
                        {orZero(c.totals.addToCarts).toLocaleString("en-US")}
                    </div>
                    <div className="cell data">
                        {orZero(c.totals.scores.addToCartConversionRate).toFixed(0)}%
                    </div>
                    <div className="cell data is-up">
                        {orZero(c.totals.scores.itemsPerCustomer).toFixed(1)}
                    </div>
                </div>

            </div>,

            activeTab == 'adsets' && (
                c.adsets.length ?
                    c.adsets.map(adset => {
                        return renderAdsetRow(adset, c, true);
                    }) :
                    <div className="row no-data-row" key={`${c.id}_no-adsets`}>No adsets exist for this campaign.</div>
            ),

            activeTab == 'ads' && (
                c.adsets.length ?
                    c.adsets
                        .filter(adset => isAdsetFiltered(adset, c))
                        .map(adset => {
                            return ([
                                renderAdsetRow(adset, c, false),

                                adset.ads.length ?
                                    adset.ads.map(ad => {
                                        return renderAdRow(ad, adset, c);
                                    }) :
                                    <div className="row no-data-row" key={`${c.id}_no-ads`}>No ads exist for this adset.</div>
                            ]);
                        }) :
                    <div className="row no-data-row" key={`${c.id}_no-adsets`}>No adsets exist for this campaign.</div>
            )

        ]);
    };

    const renderAdsetRow = (adset, campaign, linkable) => {
        return (
            <div key={adset.id} id={adset.id} className={'row adset-row data ' + `status-${adset.status}` +
                (!campaign.provider ? ' provider-unknown' : ` provider-${campaign.provider}`)}>
                <div className="item-name adset">
                    <label className="label">
                        {linkable ? <Link to="" onClick={(e) => { gotoAdset(e, adset, campaign); }}>{renderName(adset.name || adset.id)}</Link> :
                            <div>{renderName(adset.name || adset.id)}</div>
                        }
                    </label>
                </div>

                <div className="row-data costs">
                    <div className="cell data">
                        {formatCurrency(adset.totals.adspend, 2)}
                    </div>
                    <div className="cell data is-down">
                        {formatCurrency(adset.totals.cpp, 2)}
                    </div>
                </div>

                <div className="row-data orders">
                    <div className="cell data">
                        {formatCurrency(adset.totals.revenue, 2)}
                    </div>
                    <div className="cell data">
                        {adset.totals.orders.toLocaleString("en-US")}
                    </div>
                    <div className="cell data is-up">
                        {formatCurrency(adset.totals.aov, 2)}
                    </div>
                </div>

                <div className="row-data profit">
                    <div className="cell data">
                        {formatCurrency(adset.totals.profit, 2)}
                    </div>
                    <div className="cell data">
                        {adset.totals.roas.toFixed(2)}
                    </div>
                </div>

                <div className="row-data conversions">
                    <div className="cell data">
                        {orZero(adset.totals.scores.score).toFixed(0)}
                    </div>
                    <div className="cell data">
                        {orZero(adset.totals.sessions).toLocaleString("en-US")}
                    </div>
                    <div className="cell data">
                        {orZero(adset.totals.scores.conversionRate).toFixed(1)}%
                    </div>
                    <div className="cell data">
                        {orZero(adset.totals.addToCarts).toLocaleString("en-US")}
                    </div>
                    <div className="cell data">
                        {orZero(adset.totals.scores.addToCartConversionRate).toFixed(0)}%
                    </div>
                    <div className="cell data is-up">
                        {orZero(adset.totals.scores.itemsPerCustomer).toFixed(1)}
                    </div>
                </div>

            </div>
        );
    };

    const renderAdRow = (ad, adset, campaign) => {
        return (
            <div key={ad.id} id={ad.id} className={'row ad-row data' + ` status-${ad.status}` +
                (!campaign.provider ? ' provider-unknown' : ` provider-${campaign.provider}`)}>
                <div className="item-name ad">
                    <div>{renderName(ad.name || ad.id)}</div>
                </div>

                <div className="row-data costs">
                    <div className="cell data">
                        {formatCurrency(ad.totals.adspend, 2)}
                    </div>
                    <div className="cell data is-down">
                        {formatCurrency(ad.totals.cpp, 2)}
                    </div>
                </div>

                <div className="row-data orders">
                    <div className="cell data">
                        {formatCurrency(ad.totals.revenue, 2)}
                    </div>
                    <div className="cell data">
                        {ad.totals.orders.toLocaleString("en-US")}
                    </div>
                    <div className="cell data is-up">
                        {formatCurrency(ad.totals.aov, 2)}
                    </div>
                </div>

                <div className="row-data profit">
                    <div className="cell data">
                        {formatCurrency(ad.totals.profit, 2)}
                    </div>
                    <div className="cell data">
                        {ad.totals.roas.toFixed(2)}
                    </div>
                </div>

                <div className="row-data conversions">
                    <div className="cell data">
                        {ad.totals.scores.score?.toFixed(0)}
                    </div>
                    <div className="cell data">
                        {orZero(ad.totals.sessions).toLocaleString("en-US")}
                    </div>
                    <div className="cell data">
                        {orZero(ad.totals.scores.conversionRate).toFixed(1)}%
                    </div>
                    <div className="cell data">
                        {orZero(ad.totals.addToCarts).toLocaleString("en-US")}
                    </div>
                    <div className="cell data">
                        {orZero(ad.totals.scores.addToCartConversionRate).toFixed(0)}%
                    </div>
                    <div className="cell data is-up">
                        {orZero(ad.totals.scores.itemsPerCustomer).toFixed(1)}
                    </div>
                </div>

            </div>
        );
    };

    const toggleActiveOnly = (e) => {
        setActiveOnly(!activeOnly);
    };

    const toggleForceUpdate = (e) => {
        setForceUpdate(!forceUpdate);
    };

    const toggleDeleteBeforeRefresh = (e) => {
        setDeleteBeforeRefresh(!deleteBeforeRefresh);
    };

    const hasData = () => {
        return (shownData && shownData.length) ? true : false;
    }

    const renderHeader = () => (
        <div className="filter-header">
            <Dropdown className="white"
                selectedItem={metricOptions.find(o => o.value == metricOption).label}
                setSelection={onChangeMetricOption}
                items={metricOptions}
            />
            is
            <Dropdown className="white"
                selectedItem={performanceOptions.find(o => o.value == performanceOption).label}
                setSelection={onChangePerformanceOption}
                items={performanceOptions}
            />
            for

            <DateRangePicker onSetDateRange={onChangeTimeFrame} dateRange={selectedDateRange} timezone={timezone} />

            <Button className="btn-view-event" round color="white" onClick={() => refreshData()}>Refresh</Button>

            {renderAdminContent(
                <div className="dev-commands">
                    <div className="flex-col">
                        <Button className="btn-view-event" round color="white" size="small" onClick={(e) => toggleActiveOnly()}>
                            <input type="checkbox" readOnly checked={activeOnly} /> Active?
                        </Button>
                        <Button className="btn-view-event" round color="white" size="small" onClick={(e) => toggleForceUpdate()}>
                            <input type="checkbox" readOnly checked={forceUpdate} /> Force?
                        </Button>
                    </div>
                    <Button className="btn-view-event" round color="white" size="small" onClick={(e) => toggleDeleteBeforeRefresh()}>
                        <input type="checkbox" readOnly checked={deleteBeforeRefresh} /> Delete before?
                    </Button>
                    <Button className="btn-view-event" round color="white" size="small" onClick={() => deleteCurrentData_DEV_ONLY(true)}>Delete Data (local)</Button>
                </div>,
                user
            )}

            {renderAdminContent(
                <div className="admin-controls">

                    <div className="account-info">
                        <div className="account-id">
                            Account ID:
                            <b>{account._id}</b>
                        </div>
                        <IconCopy onClick={() => copyToClipboard(account._id)} />
                    </div>

                    <div className="tracking-info">
                        {
                            (account.tracking?.trackingId) ?
                                <>
                                    <div className="tracking-id">
                                        Tracking ID:
                                        <b>{account.tracking.trackingId}</b>
                                    </div>
                                    <IconCopy onClick={() => copyToClipboard(account.tracking.trackingId)} />
                                </> :
                                <>(Tracking not initialized.)</>
                        }
                    </div>

                    <div className="timezone-info">
                        <div className="today">
                            Today Start (store):
                            <b>{formatIsoShort(todayStart(timezone))} (UTC)</b>
                            {timezone}
                        </div>
                        <IconCopy onClick={() => copyToClipboard(todayStart(timezone).toISOString())} />
                    </div>
                </div>,
                user
            )
            }

            {/* {lastUpdated &&
                <div className="last-updated">
                    Last updated: {lastUpdated}
                </div>
            } */}
        </div >
    );

    const renderDataView = () => (
        <div className="wrapper data-table">

            <div className={`tab-content ${activeTab}`}>

                <div className="row header">

                    <div className="col col-nav">
                        <div className={'btn cell campaigns' + (activeTab == 'campaigns' ? ' active' : '')} onClick={() => { setActiveTab('campaigns'); setSelectedAdsets([]); }}>
                            Campaigns
                        </div>
                        <div className={'btn cell adsets' + (activeTab == 'adsets' ? ' active' : '')} onClick={() => { setActiveTab('adsets'); setSelectedAdsets([]); }}>
                            Adsets
                        </div>
                        <div className={'btn cell ads' + (activeTab == 'ads' ? ' active' : '')} onClick={() => setActiveTab('ads')}>
                            Ads
                        </div>
                    </div>

                    <div className="col col-header costs">
                        <div className="title">
                            Costs
                        </div>

                        <div className="cols">
                            <div className="cell title">
                                Adspend
                            </div>
                            <div className="cell title">
                                Cost Per Purchase
                            </div>
                        </div>
                    </div>

                    <div className="col col-header orders">
                        <div className="title">
                            Revenue
                        </div>

                        <div className="cols">
                            <div className="cell title">
                                R   evenue
                            </div>
                            <div className="cell title">
                                Orders
                            </div>
                            <div className="cell title">
                                AOV
                            </div>
                        </div>
                    </div>

                    <div className="col col-header profit">
                        <div className="title">
                            Profit
                        </div>

                        <div className="cols">
                            <div className="cell title">
                                Profit
                            </div>
                            <div className="cell title">
                                ROAS
                            </div>
                        </div>
                    </div>

                    <div className="col col-header conversions">
                        <div className="title">
                            Traffic Quality
                        </div>

                        <div className="cols">
                            <div className="cell title">
                                Score
                            </div>
                            <div className="cell title">
                                Sessions
                            </div>
                            <div className="cell title">
                                Conversion %
                            </div>
                            <div className="cell title">
                                ATCs
                            </div>
                            <div className="cell title">
                                ATC Conv. %
                            </div>
                            <div className="cell title">
                                Items/Cust.
                            </div>
                        </div>
                    </div>

                </div>

                <div className="scroll-content">

                    {shownData
                        .filter(c => isCampaignFiltered(c))
                        .map(c => {
                            return renderCampaignRow(c);
                        })
                    }

                </div>

                {shownTotals &&
                    <div className="row totals">

                        <div className="col col-campaign-total cell">
                            Total of {shownTotals.numItems} {capitalize(activeTab)}
                        </div>

                        <div className="col col-total costs">
                            <div className="cell data">
                                {formatCurrency(shownTotals.totals.adspend, 2)}
                            </div>
                            <div className="cell data">
                                {formatCurrency(shownTotals.totals.cpp, 2)}
                            </div>
                        </div>

                        <div className="col col-total orders">
                            <div className="cell data">
                                {formatCurrency(shownTotals.totals.revenue, 2)}
                            </div>
                            <div className="cell data">
                                {shownTotals.totals.orders?.toLocaleString("en-US")}
                            </div>
                            <div className="cell data">
                                {formatCurrency(shownTotals.totals.aov, 2)}
                            </div>
                        </div>

                        <div className="col col-total profit">
                            <div className="cell data">
                                {formatCurrency(shownTotals.totals.profit, 2)}
                            </div>
                            <div className="cell data">
                                {shownTotals.totals.roas.toFixed(2)}
                            </div>
                        </div>

                        <div className="col col-total conversions">
                            <div className="cell data">
                                100
                            </div>
                            <div className="cell data">
                                {orZero(shownTotals.totals.sessions).toLocaleString("en-US")}
                            </div>
                            <div className="cell data">
                                {orZero(shownTotals.totals.scores.conversionRate).toFixed(1)}%
                            </div>
                            <div className="cell data">
                                {orZero(shownTotals.totals.addToCarts).toLocaleString("en-US")}
                            </div>
                            <div className="cell data">
                                {orZero(shownTotals.totals.scores.addToCartConversionRate).toFixed(0)}%
                            </div>
                            <div className="cell data is-up">
                                {orZero(shownTotals.totals.scores.itemsPerCustomer).toFixed(1)}
                            </div>
                        </div>

                    </div>
                }

            </div>
            {/*
            {activeTab == 'adsets' &&
                <div className="tab-content col right-col" id="tab-content-adsets">
                    adsets content
                </div>
            }

            {activeTab == 'ads' &&
                <div className="tab-content col right-col" id="tab-content-ads">
                    ads content
                </div>
            } */}

        </div>
    );

    return (
        <div className={'ads-manager-page page-layout ' + (scroll.direction == 'up' ? 'show-menu' : '')}>

            <Helmet><title>{Config.SITE_TITLE_PREFIX} - Ads Manager</title></Helmet>

            {(confirmDelete != 0) &&
                <Dialog open={confirmDelete} title={`Delete all ${confirmDelete == 2 ? 'data (local only)?' : 'data?'}`}
                    body={<div className="body">
                        Are you sure you want to delete all of this account's provider data for the selected date range?
                        {/* {
                            <input type="checkbox" value="All" label="All providers" onChange={() => setDeleteAll(e.target.checked)} />
                            <input type="checkbox" value="Google" label="Google" onChange={(e) => alert(e)} />
                        } */}
                    </div>}
                    actions={[
                        <Button key="a" className="btn-view-event" round color="red" onClick={() => setConfirmDelete(0)}>Cancel</Button>,
                        <Button key="b" className="btn-view-event" round filled color="red" onClick={() => onConfirmDelete(false)}>Delete</Button>
                    ]}
                    onClose={() => setConfirmDelete(0)} />
            }

            {renderHeader()}

            {loading ? renderLoader() :

                // error
                error ? renderError(ErrorMessages.defaultErrorMessage(), error, "large")

                    // loaded and has data
                    : hasData() ? renderDataView()

                        // loaded but has no data
                        : renderNoDataMessage()

            }

        </div>
    );
};